import { useState, useEffect, useRef, useCallback } from "react";

const TYPING_SPEED = 55; // ms per character
const DELETING_SPEED = 30; // ms per character
const PAUSE_AFTER_TYPING = 2000; // ms
const PAUSE_AFTER_DELETING = 400; // ms

/**
 * Typewriter placeholder hook — cycles through example questions with a
 * type-then-delete animation. Pauses when the input is focused or has a
 * real user value.
 *
 * @param {string[]} phrases - Array of example strings to cycle through.
 * @returns {{ displayText: string, isAnimating: boolean, onFocus: () => void, onBlur: () => void, setUserActive: (active: boolean) => void }}
 */
const useTypewriterPlaceholder = (phrases) => {
  const [displayText, setDisplayText] = useState("");
  const [isAnimating, setIsAnimating] = useState(true);

  // Refs to avoid stale closures
  const phraseIdx = useRef(0);
  const charIdx = useRef(0);
  const isDeleting = useRef(false);
  const pauseUntil = useRef(0);
  const animFrame = useRef(null);
  const lastTick = useRef(0);
  const userActive = useRef(false); // focused or has user text

  const tick = useCallback(
    (now) => {
      if (userActive.current) {
        animFrame.current = requestAnimationFrame(tick);
        return;
      }

      if (now < pauseUntil.current) {
        animFrame.current = requestAnimationFrame(tick);
        return;
      }

      const speed = isDeleting.current ? DELETING_SPEED : TYPING_SPEED;
      if (now - lastTick.current < speed) {
        animFrame.current = requestAnimationFrame(tick);
        return;
      }
      lastTick.current = now;

      const currentPhrase = phrases[phraseIdx.current % phrases.length];

      if (!isDeleting.current) {
        // Typing
        charIdx.current += 1;
        const text = currentPhrase.slice(0, charIdx.current);
        setDisplayText(text);

        if (charIdx.current >= currentPhrase.length) {
          isDeleting.current = true;
          pauseUntil.current = now + PAUSE_AFTER_TYPING;
        }
      } else {
        // Deleting
        charIdx.current -= 1;
        const text = currentPhrase.slice(0, charIdx.current);
        setDisplayText(text);

        if (charIdx.current <= 0) {
          isDeleting.current = false;
          phraseIdx.current = (phraseIdx.current + 1) % phrases.length;
          pauseUntil.current = now + PAUSE_AFTER_DELETING;
        }
      }

      animFrame.current = requestAnimationFrame(tick);
    },
    [phrases]
  );

  useEffect(() => {
    lastTick.current = performance.now();
    animFrame.current = requestAnimationFrame(tick);
    return () => {
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, [tick]);

  const onFocus = useCallback(() => {
    userActive.current = true;
    setDisplayText("");
    setIsAnimating(false);
  }, []);

  const onBlur = useCallback(() => {
    // Will be called externally only when input is empty
    // Restart animation from beginning of next phrase
    userActive.current = false;
    phraseIdx.current = (phraseIdx.current) % phrases.length;
    charIdx.current = 0;
    isDeleting.current = false;
    pauseUntil.current = performance.now() + 600;
    setIsAnimating(true);
  }, [phrases]);

  const setUserActive = useCallback(
    (active) => {
      if (active) {
        userActive.current = true;
        setDisplayText("");
        setIsAnimating(false);
      } else {
        userActive.current = false;
        charIdx.current = 0;
        isDeleting.current = false;
        pauseUntil.current = performance.now() + 600;
        setIsAnimating(true);
      }
    },
    []
  );

  return { displayText, isAnimating, onFocus, onBlur, setUserActive };
};

export default useTypewriterPlaceholder;
